import React, { createContext, useContext } from 'react'
import { API_BASE_URL } from '../config'

interface AdminAuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  login: (password: string) => Promise<boolean>
  logout: () => void
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

const ADMIN_TOKEN_KEY = 'mc_admin_token'

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [token, setToken] = React.useState<string | null>(null)

  React.useEffect(() => {
    try {
      const storedToken = localStorage.getItem(ADMIN_TOKEN_KEY)
      if (storedToken) {
        setToken(storedToken)
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Failed to read admin auth state from localStorage:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const login = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      })

      if (!response.ok) {
        return false
      }

      const data = await response.json()

      try {
        localStorage.setItem(ADMIN_TOKEN_KEY, data.token)
      } catch (error) {
        console.error('Failed to persist admin auth state to localStorage:', error)
      }

      setToken(data.token)
      setIsAuthenticated(true)
      return true
    } catch (error) {
      console.error('Admin login error:', error)
      return false
    }
  }

  const logout = () => {
    try {
      localStorage.removeItem(ADMIN_TOKEN_KEY)
    } catch (error) {
      console.error('Failed to clear admin auth state from localStorage:', error)
    }

    setIsAuthenticated(false)
    setToken(null)
  }

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, isLoading, token, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}
